import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { Location } from 'history';

import { HtmlEditor } from 'modules/graphql/types';
import { removeAllToasts } from 'modules/toastContainer';

import { useSaveTemplate } from 'modules/templateEditor/hooks/useSaveTemplate';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { isParameterOnlyChange } from 'utils/urlParams';

export enum UnsavedChangesModals {
  None = 'none',
  React = 'react',
}

export const UnsavedChangesPrompts = () => {
  const history = useHistory();
  const isCreativeLibraryAssetsFolderPanelFFEnabled = useFeatureFlag(
    'CreativeLibraryAssetsFolderPanel',
    false,
  );
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState<UnsavedChangesModals>(UnsavedChangesModals.None);
  const {
    contentEditor,
    isDirty,
    setDragAndDropInitTs,
    setDragAndDropInitValue,
    skipUnsavedChangesCheck,
  } = useEditableFieldsContext();
  const { saveTemplate } = useSaveTemplate();
  const { refetch } = useTemplateQueryContext();

  const [nextLocation, setNextLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);

  // prompt user about unsaved changes with custom modal (navigating within react-router)
  const promptForUnsavedChanges = (nextLoc: Location): boolean => {
    if (!confirmedNavigation) {
      if (skipUnsavedChangesCheck.current) {
        skipUnsavedChangesCheck.current = false;
        return true;
      }

      // Allow parameter-only changes without prompting
      if (
        isCreativeLibraryAssetsFolderPanelFFEnabled &&
        isParameterOnlyChange(history.location, nextLoc, ['folderId'])
      ) {
        return true;
      }

      setOpenModal(UnsavedChangesModals.React);
      setNextLocation(nextLoc);
      return false; // block the navigation
    }
    skipUnsavedChangesCheck.current = false;
    return true; // allow the navigation
  };

  const handleLeaveWithoutSaving = () => {
    setOpenModal(UnsavedChangesModals.None);
    refetch(); // revert changes
    if (contentEditor === HtmlEditor.Bee) {
      setDragAndDropInitValue();
      setDragAndDropInitTs();
    }
    setConfirmedNavigation(true);
  };

  const handleSaveAndExit = () => {
    setOpenModal(UnsavedChangesModals.None);
    saveTemplate(() => {
      refetch(); // reset the clean state
      dispatch(removeAllToasts()); // remove all toasts since the layout will unmount & remount
      setConfirmedNavigation(true);
    });
  };

  useEffect(() => {
    if (confirmedNavigation && nextLocation) {
      // reset confirmedNavigation (this component doesn't unmount when switching views);
      setConfirmedNavigation(false);
      // navigate to the intended blocked location
      history.push(nextLocation as any as string);
    }
  }, [confirmedNavigation, nextLocation]);

  // prompt user about unsaved changes with browser dialog (navigating outside of react-router)
  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();

      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <>
      <Prompt when={isDirty} message={promptForUnsavedChanges} />

      {openModal === UnsavedChangesModals.React && (
        <UnsavedChangesModal
          onClose={() => setOpenModal(UnsavedChangesModals.None)}
          onLeave={handleLeaveWithoutSaving}
          onSave={handleSaveAndExit}
        />
      )}
    </>
  );
};
